import { GameConstants } from "../../../GameConstants";
import { GameVars } from "../../../GameVars";
import { BoardContainer } from "../BoardContainer";
import { BoardScene } from "../BoardScene";
import { DebugGraphics } from "../DebugGraphics";
import { StrikerSpeedSetter } from "../gui/StrikerSpeedSetter";
import { BoardManager } from "./BoardManager";
import * as Carrom from "carrom-king-engine";

export class BoardManagerPVBot extends BoardManager {

    private bot: Carrom.Bot;
    private turnsMissed: number;

    constructor() {
    
        super();

        this.bot = new Carrom.Bot(Phaser.Math.RND, GameVars.carromConfig);
        this.turnsMissed = 0;

        if (GameConstants.DEVELOPMENT && GameConstants.SHOW_DEBUG_GRAPHICS) {
            this.bot.addEventListener(Carrom.Event.DRAW_CIRCLE, this.drawDebugCircle, this);
            this.bot.addEventListener(Carrom.Event.DRAW_LINE, this.drawDebugLine, this);
        }
        
        const self = this;

        setTimeout(function(): void {
            self.onBothPlayersReady(GameVars.playerData.id);
        }, 1000);
    }

    public onBothPlayersReady(startingPlayerId: string): void {

        super.onBothPlayersReady(startingPlayerId);

        const self = this;

        setTimeout(function(): void {
            self.startTimer();
        }, 1000);
    }

    public shoot(strikerPosition: Carrom.Vector2D, strikerVelocity: Carrom.Vector2D): void {

        this.stopTimer(); 

        if (GameVars.currentTurnPlayerId === GameVars.playerData.id) {
            this.turnsMissed = 0;
        }

        super.shoot(strikerPosition, strikerVelocity);
    }

    protected onTurnCompleted(
        strikerPosition: Carrom.Vector2D, 
        pucks: Carrom.Puck [], 
        pocketedPucks: Carrom.Puck [], 
        alternatePlayer: boolean, 
        hasQueenBeenPocketed: boolean, 
        restituteQueen: boolean, 
        skipQueenCovering: boolean,
        score: number,
        hasPlayerWon: boolean,
        hasAdversaryWon: boolean,
        faults: string []
        ): void {

        super.onTurnCompleted(
            strikerPosition, 
            pucks, 
            pocketedPucks, 
            alternatePlayer, 
            hasQueenBeenPocketed, 
            restituteQueen, 
            skipQueenCovering, 
            score, 
            hasPlayerWon, 
            hasAdversaryWon,
            faults
        );

        this.onValidTurn();

        if (!GameVars.gameOver) {

            this.startTimer();

            if (GameVars.currentTurnPlayerId === GameVars.adversaryData.id) {

                this.shootBot();
            } 
        }
    }

    public onSecondPassed(): void {

        super.onSecondPassed();

        if (GameVars.time >= 17) { 
            
            this.turnsMissed ++;
            this.stopTimer();

            if (this.turnsMissed === GameConstants.MISSED_TURNS_GAME_OVER_FORCED) {
                this.onGameOver(GameVars.adversaryData.id);
            } else {
                this.onTimeOver(GameVars.adversaryData.id, this.turnsMissed);
            }
        }
    }

    public onTimeOver(currentTurnPlayerId: string, turnMissed: number): void {

        super.onTimeOver(currentTurnPlayerId, turnMissed);

        this.startTimer();

        this.shootBot();
    }

    private startTimer(): void {
        
        GameVars.time = 0;

        this.timerEvent = BoardScene.currentInstance.time.addEvent({ delay: 1000, loop: true, callback: this.onSecondPassed, callbackScope: this });
    }

    private stopTimer(): void {

        if (!this.timerEvent) {
            return;
        }

        GameVars.time = 0;
        
        this.timerEvent.destroy();
        this.timerEvent = null;
    }

    private drawDebugLine(segment: {p1: Carrom.Vector2D, p2: Carrom.Vector2D}, color): void {

        const x1 = segment.p1.x * GameConstants.PHYS_SCALE;
        const y1 = segment.p1.y * GameConstants.PHYS_SCALE;

        const x2 = segment.p2.x * GameConstants.PHYS_SCALE;
        const y2 = segment.p2.y * GameConstants.PHYS_SCALE;

        DebugGraphics.currentInstance.drawLine(x1, y1, x2, y2, color);
    }

    private drawDebugCircle(center: Carrom.Vector2D, radius: number, color: number): void {

        const x = center.x * GameConstants.PHYS_SCALE;
        const y = center.y * GameConstants.PHYS_SCALE;
        const r = radius * GameConstants.PHYS_SCALE;

        DebugGraphics.currentInstance.drawCircle(x, y, r, color);
    }

    private shootBot(): void {

        const shotData = this.bot.getShot(GameVars.pucks, GameVars.gameMode, GameVars.adversaryColor);

        // depending on players skill level the shot is diverted
        let skillLevel = GameVars.difficulty === Carrom.Difficulty.EXPERT ? GameVars.gameData.skillLevel + 1.5 : GameVars.gameData.skillLevel;

        if (skillLevel > 10) {
            skillLevel = 10;
        }

        if (Math.random() > skillLevel / 10) {

            if (Math.random() > .5) {
                shotData.strikerVelocity = shotData.strikerVelocity.times(.9);
            }

            let angle = Math.atan2(shotData.strikerVelocity.y, shotData.strikerVelocity.x);
            angle = Math.random() > .5 ? angle + 3.5 * Math.PI / 180 : angle - 3.5 * Math.PI / 180;
            
            const strikerVelocity = shotData.strikerVelocity.magnitude;
            shotData.strikerVelocity = new Carrom.Vector2D(strikerVelocity * Math.cos(angle), strikerVelocity * Math.sin(angle));
        }
    
        const self = this;

        const gui = BoardScene.currentInstance.gui;
        const adversaryStrikerSpeedSetter = gui.adversaryStrikerSpeedSetter;

        const t1 = 1000 + 2000 * Math.random();
        const t2 = t1 + 1000 + 2000 * Math.random();
        const t3 = t2 + 1000 + 3000 * Math.random();

        setTimeout(function(): void {

            if (GameConstants.SHOW_DEBUG_GRAPHICS) {
                DebugGraphics.currentInstance.clear();
            }
            
            BoardContainer.currentInstance.strikerForcedPx = shotData.strikerPosition.x;
            
        }, t1);

        setTimeout(function(): void {

            adversaryStrikerSpeedSetter.activate();

            adversaryStrikerSpeedSetter.angleAdversary = Math.atan2(shotData.strikerVelocity.y, shotData.strikerVelocity.x);
            adversaryStrikerSpeedSetter.rAdversary = shotData.strikerVelocity.magnitude / GameConstants.MAX_STRIKER_SPEED * StrikerSpeedSetter.MAX_RADIUS;

        }, t2);

        setTimeout(function(): void {
            adversaryStrikerSpeedSetter.deactivate();
            self.shoot(shotData.strikerPosition, shotData.strikerVelocity);
        }, t3);
    }
}
